import apiClient from '@/utils/apiClient';

/**
 * Products hidden from the offer program must not appear in the
 * price-change / new-product notification. Missing flags stay included;
 * the backend drops them when the saved item is not visible.
 * @param {Object} draft
 * @returns {boolean}
 */
function draftIsHiddenFromOfferApp(draft) {
    const payload = draft?.payload || {};
    const detail = payload.pricing_detail;
    const form = draft?.builderState?.productBuilderForm;
    const flags = [
        payload.visibleInOfferApp,
        payload.visible_in_offer_app,
        detail && typeof detail === 'object' ? detail.visibleInOfferApp : undefined,
        form?.visibleInOfferApp,
    ];
    return flags.some((value) => value === false);
}

/**
 * Sale price of the default furnizor, when the draft carries furnizor tabs.
 * Product-level `payload.price` is that same price; a non-default tab must
 * not be used for the notification.
 *
 * @param {Object} draft
 * @returns {number|null}
 */
function readDefaultFurnizorSalePrice(draft) {
    const detail = draft?.payload?.pricing_detail;
    const tabs = Array.isArray(detail?.furnizorPricings)
        ? detail.furnizorPricings
        : (Array.isArray(detail?.furnizor_pricings) ? detail.furnizor_pricings : []);
    const defaultTab = tabs.find((tab) => (
        tab && (tab.isDefault === true || tab.is_default === true)
    ));
    if (!defaultTab) {
        return null;
    }
    const overrideOn = Boolean(
        defaultTab.overrideListPriceRonIncVatFinal
        ?? defaultTab.override_list_price_ron_inc_vat_final,
    );
    if (overrideOn) {
        const override = Number(
            defaultTab.overrideListPriceRonIncVatFinalValue
            ?? defaultTab.override_list_price_ron_inc_vat_final_value,
        );
        if (Number.isFinite(override)) {
            return override;
        }
    }
    const sale = Number(
        defaultTab.listPriceRonIncVatFinal
        ?? defaultTab.list_price_ron_inc_vat_final,
    );
    return Number.isFinite(sale) ? sale : null;
}

/**
 * Extracts products that should trigger agent notification after publish.
 * Includes new product codes and sale price changes of the default furnizor
 * only, and only when the product is visible in the offer program.
 * A price change on a non-default furnizor does not send email.
 *
 * @param {Array<Object>} drafts
 * @returns {Array<Object>}
 */
export function extractPriceListNotificationChanges(drafts = []) {
    const changes = [];

    drafts.forEach((draft) => {
        const productCode = String(
            draft.payload?.product_code || draft.product_code || '',
        ).trim();
        if (!productCode || draftIsHiddenFromOfferApp(draft)) {
            return;
        }

        const productName = draft.payload?.product_name
            || draft.summary?.product_name
            || '';
        const newPrice = Number(draft.payload?.price);
        const previousPriceRaw = draft.previousPrice ?? draft.previous_price ?? null;
        const previousPrice = previousPriceRaw == null || previousPriceRaw === ''
            ? null
            : Number(previousPriceRaw);
        const isNewProduct = draft.action === 'create';

        if (!Number.isFinite(newPrice)) {
            return;
        }

        if (isNewProduct) {
            changes.push({
                product_code: productCode,
                product_name: productName,
                old_price: null,
                new_price: newPrice,
                is_new_product: true,
            });
            return;
        }

        // Email follows the default furnizor sale price. A change only on a
        // non-default furnizor leaves that price in place and must not notify.
        const defaultSale = readDefaultFurnizorSalePrice(draft);
        const comparableNew = defaultSale != null ? defaultSale : newPrice;
        if (previousPrice == null || Math.abs(previousPrice - comparableNew) <= 0.001) {
            return;
        }

        changes.push({
            product_code: productCode,
            product_name: productName,
            old_price: previousPrice,
            new_price: comparableNew,
            is_new_product: false,
        });
    });

    return changes;
}

/**
 * Notify agents about published price list changes.
 *
 * @param {Object} params
 * @param {number|string} params.priceListId
 * @param {boolean} [params.isNewList]
 * @param {Array<Object>} params.changes
 * @param {boolean} [params.test]
 * @returns {Promise<Object>}
 */
export async function notifyPriceListChanges({
    priceListId,
    isNewList = false,
    changes = [],
    test = true,
}) {
    if (!priceListId || !changes.length) {
        return null;
    }

    const response = await apiClient.post('/api/notify-price-list-changes/', {
        price_list_id: priceListId,
        is_new_list: isNewList,
        changes,
        test,
    });
    return response.data;
}
